import type { GuideTourRegistry } from '../types'
import { isGuideTourSeen } from '../persistence'
import { isGuideTourActivatable } from './activation'
import {
  getPendingGuideToursForPathname,
  selectGuideTourActivationCandidate,
} from './utils'

export const GUIDE_TOUR_ROUTE_CHAIN_COOLDOWN_MS = 800

export type GuideTourRouteQueueInfo = {
  position: number
  total: number
}

/**
 * Unseen tours on the route whose target exists in the DOM (or has no target).
 */
export const getActivatableRouteToursForPathname = (
  pathname: string,
  registry: GuideTourRegistry,
  isSeen: typeof isGuideTourSeen = isGuideTourSeen
) =>
  getPendingGuideToursForPathname(pathname, registry, isSeen)
    .filter(candidate =>
      isGuideTourActivatable(candidate.tour, candidate.stepIndex)
    )
    .map(candidate => candidate.tour)

/**
 * Position among tours that can actually run on this route right now.
 */
export const getRouteTourQueueInfo = (
  pathname: string,
  registry: GuideTourRegistry,
  activeTourId: string,
  isSeen: typeof isGuideTourSeen = isGuideTourSeen
): GuideTourRouteQueueInfo | null => {
  const activatableTours = getActivatableRouteToursForPathname(
    pathname,
    registry,
    isSeen
  )

  if (activatableTours.length <= 1) {
    return null
  }

  const position =
    activatableTours.findIndex(tour => tour.id === activeTourId) + 1

  if (position <= 0) {
    return null
  }

  return {
    position,
    total: activatableTours.length,
  }
}

/** Always returns queue position on the route; defaults to 1 of 1 when alone. */
export const getRouteTourAnalyticsQueueInfo = (
  pathname: string,
  registry: GuideTourRegistry,
  activeTourId: string,
  isSeen: typeof isGuideTourSeen = isGuideTourSeen
): GuideTourRouteQueueInfo => {
  const chainInfo = getRouteTourChainSessionInfo(pathname, activeTourId)

  if (chainInfo) {
    return chainInfo
  }

  const queueInfo = getRouteTourQueueInfo(
    pathname,
    registry,
    activeTourId,
    isSeen
  )

  if (queueInfo) {
    return queueInfo
  }

  return { position: 1, total: 1 }
}

export const getPrimaryPendingRouteTour = (
  pathname: string,
  registry: GuideTourRegistry,
  isSeen: typeof isGuideTourSeen = isGuideTourSeen
) => selectGuideTourActivationCandidate(pathname, registry, isSeen)?.tour

export const isGuideTourRouteChainCooldownActive = (
  chainCooldownUntil: number
): boolean => chainCooldownUntil > Date.now()

/** True when other unseen tours on the same route remain after `completedTour`. */
export const hasMoreUnseenRouteToursAfter = (
  pathname: string,
  registry: GuideTourRegistry,
  completedTour: { id: string; version: number }
): boolean => {
  const treatCurrentAsSeen = (tourId: string, version: number) => {
    if (tourId === completedTour.id && version === completedTour.version) {
      return true
    }

    return isGuideTourSeen(tourId, version)
  }

  return (
    getPendingGuideToursForPathname(pathname, registry, treatCurrentAsSeen)
      .length > 0
  )
}

export type GuideTourRouteChainSession = {
  pathname: string
  tourIds: readonly string[]
}

let activeRouteTourChainSession: GuideTourRouteChainSession | null = null

/**
 * Captures the full pending queue when the first tour in a multi-tour route starts.
 * Later tours in the chain stay in this session even after earlier ones are marked seen.
 */
export const beginRouteTourChainSession = (
  pathname: string,
  registry: GuideTourRegistry
): void => {
  if (activeRouteTourChainSession?.pathname === pathname) {
    return
  }

  const pending = getPendingGuideToursForPathname(pathname, registry)

  if (pending.length <= 1) {
    return
  }

  activeRouteTourChainSession = {
    pathname,
    tourIds: pending.map(candidate => candidate.tour.id),
  }
}

export const getRouteTourChainSessionInfo = (
  pathname: string,
  activeTourId: string
): GuideTourRouteQueueInfo | null => {
  if (activeRouteTourChainSession?.pathname !== pathname) {
    return null
  }

  const position = activeRouteTourChainSession.tourIds.indexOf(activeTourId) + 1

  if (position <= 0) {
    return null
  }

  return {
    position,
    total: activeRouteTourChainSession.tourIds.length,
  }
}

export const isRouteTourChainSessionActive = (pathname: string): boolean =>
  activeRouteTourChainSession?.pathname === pathname &&
  (activeRouteTourChainSession?.tourIds.length ?? 0) > 1

export const clearRouteTourChainSession = (): void => {
  activeRouteTourChainSession = null
}

/** Test helper to reset module session state between tests. */
export const resetRouteTourChainSessionForTests = (): void => {
  activeRouteTourChainSession = null
}
